import React from 'react';

// Utility function to format duration for alerts
export function formatAlertDuration(allRows) {
	if (!allRows || allRows.length === 0) return 'Just started';
	
	const latest = allRows[0];
	if (!latest || typeof latest.ts !== 'number') return 'Just started';
	
	// Find when the outage started (oldest consecutive down entry)
	// Rows are ordered most recent first, so we iterate forward to find the start
	let downStart = latest.ts;
	for (let i = 1; i < allRows.length; i++) {
		const row = allRows[i];
		if (!row || typeof row.ts !== 'number') break;
		if (row.ok) break; // Found the transition point (was up before this)
		downStart = row.ts; // Keep going back to find when it first went down
	}
	
	const now = Date.now();
	const durationMs = now - downStart;
	const durationMinutes = Math.floor(durationMs / (60 * 1000));
	const durationHours = Math.floor(durationMinutes / 60);
	const durationDays = Math.floor(durationHours / 24);
	
	if (durationDays > 0) {
		return `Ongoing for ${durationDays} day${durationDays !== 1 ? 's' : ''}`;
	}
	if (durationHours > 0) {
		return `Ongoing for ${durationHours} hour${durationHours !== 1 ? 's' : ''}`;
	}
	if (durationMinutes > 0) {
		return `Ongoing for ${durationMinutes} minute${durationMinutes !== 1 ? 's' : ''}`;
	}
	// Less than a minute
	const durationSeconds = Math.floor(durationMs / 1000);
	return `Ongoing for ${durationSeconds} second${durationSeconds !== 1 ? 's' : ''}`;
}

// Utility function to calculate active alerts (same logic as LatestAlerts)
export function calculateAlerts(monitors = [], histories = {}) {
	return monitors
		.filter(monitor => monitor.enabled !== false) // Only check enabled monitors
		.map(monitor => {
			const rows = histories[monitor.id] || [];
			if (rows.length === 0) return null;
			
			// Ensure rows are sorted by timestamp (most recent first)
			const sortedRows = [...rows].sort((a, b) => (b.ts || 0) - (a.ts || 0));
			const latest = sortedRows[0];
			
			// Only show alerts for monitors that are currently down
			if (!latest || latest.ok !== false) return null;
			
			// Pass all rows to find when the outage actually started
			const duration = formatAlertDuration(sortedRows);
			
			return {
				id: monitor.id,
				name: monitor.name,
				url: monitor.url,
				icon: '●',
				time: new Date(latest.ts).toLocaleString('en-US', {
					month: 'short',
					day: 'numeric',
					year: 'numeric',
					hour: '2-digit',
					minute: '2-digit',
					second: '2-digit',
					timeZoneName: 'short'
				}),
				duration,
				notified: 'Slack hook to #notifyme',
				timestamp: latest.ts
			};
		})
		.filter(Boolean)
		.sort((a, b) => b.timestamp - a.timestamp); // Sort by most recent first
}

export default function NotificationsDropdown({ alerts = [], onClose, onNavigate, onOpenSiteDetails }) {
	if (alerts.length === 0) {
		return (
			<div className="notifications-dropdown">
				<div className="notifications-header">
					<h3>Notifications</h3>
					<button 
						className="notifications-close" 
						onClick={onClose}
						aria-label="Close notifications"
					>
						<span className="material-icons">close</span>
					</button>
				</div>
				<div className="notifications-empty">
					<span className="material-icons">notifications_none</span>
					<p>No active alerts</p>
				</div>
			</div>
		);
	}

	return (
		<div className="notifications-dropdown">
			<div className="notifications-header">
				<h3>Notifications ({alerts.length})</h3>
				<button 
					className="notifications-close" 
					onClick={onClose}
					aria-label="Close notifications"
				>
					<span className="material-icons">close</span>
				</button>
			</div>
			<div className="notifications-list">
				{alerts.map((alert, index) => (
					<div key={alert.id || index} className="notification-item">
						<div className="notification-icon">
							<span className="alert-icon">{alert.icon}</span>
						</div>
						<div className="notification-content">
							<div className="notification-name">{alert.name}</div>
							<div className="notification-url">{alert.url}</div>
							<div className="notification-meta">
								<span className="notification-time">{alert.time}</span>
								<span className="notification-duration">{alert.duration}</span>
							</div>
						</div>
						<button 
							className="notification-action"
							onClick={(e) => {
								e.stopPropagation();
								if (alert.id && onOpenSiteDetails) {
									onOpenSiteDetails(alert.id);
								}
							}}
							aria-label={`View details for ${alert.name}`}
						>
							<span className="material-icons">arrow_forward</span>
						</button>
					</div>
				))}
			</div>
			{alerts.length > 0 && (
				<div className="notifications-footer">
					<button 
						className="notifications-view-all"
						onClick={() => {
							if (onNavigate) onNavigate('incidents');
							if (onClose) onClose();
						}}
					>
						View all alerts
					</button>
				</div>
			)}
		</div>
	);
}

